// VRG-674 — react-query hooks for GTM → Articles approvals; VRG-685 — and publication.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { gtmApi } from 'services'

const KEY = ['internal', 'gtm', 'article-approvals'] as const

export const useArticleApprovals = () =>
  useQuery({
    queryKey: KEY,
    queryFn: () => gtmApi.articleApprovals(),
    staleTime: 60 * 1000,
    retry: false,
  })

export const useApproveArticle = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: gtmApi.approveArticle,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export const useRevokeArticleApproval = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ section, slug }: { section: string; slug: string }) => gtmApi.revokeArticleApproval(section, slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

// ── VRG-685 — publication ───────────────────────────────────────────────────────────────
const PUB_KEY = ['internal', 'gtm', 'article-publications'] as const

export const useArticlePublications = () =>
  useQuery({
    queryKey: PUB_KEY,
    queryFn: () => gtmApi.articlePublications(),
    staleTime: 30 * 1000,
    retry: false,
  })

const usePublicationMutation = <A, R>(fn: (a: A) => Promise<R>) => {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: PUB_KEY }) })
}

export const usePublishArticle = () => usePublicationMutation(gtmApi.publishArticle)
export const useUnpublishArticle = () =>
  usePublicationMutation(({ section, slug }: { section: string; slug: string }) => gtmApi.unpublishArticle(section, slug))
export const useRefreshArticleOnWebsite = () =>
  usePublicationMutation(({ section, slug }: { section: string; slug: string }) =>
    gtmApi.refreshArticleOnWebsite(section, slug),
  )
